import clsx from 'clsx'
import {getIn, useFormik} from 'formik'
import {cloneDeep, compact, get, isEmpty} from 'lodash'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {
  FORMAT_DATE,
  OPTION_COUNTRIES,
  OPTION_WEIGHT_UNIT,
  WEIGHT_UNIT,
  DIMENSION_UNIT,
} from '../../../../../gori/constants'
import {convertUnit, convertUserTimeZone} from '../../../../../gori/helpers'
import {useAuth, useStatesGlobal} from '../../../../../gori/providers'
import {Button, InputTextFormik, SelectFormik} from '../../../../../gori/widgets'
import {FromOrToModal} from '../../../common'
import {A1_CONSTANTS} from '../../core/constants'
import {UseYupValidate} from '../../../../../gori/hooks'
import * as Yup from 'yup'
import {PackageInfo} from './order/PackageInfo'
import {getErrorPaths, getKeyOrder} from '../../../utils'

type Props = {
  show: boolean
  data: any
  handleClose: () => void
}

const OrdersConfig = {
  PARCEL: ['length', 'width', 'height', 'weight', 'dimension_unit', 'weight_unit', 'package_count'],
}

const EditOrderInfo: React.FC<Props> = ({show, data, handleClose}) => {
  const intl = useIntl()
  const {currentUser} = useAuth()
  const row = useRef({parcels: 0, order_items: -1})
  const [fromToModal, setFormToModal] = useState<{
    show: boolean
    name: 'from' | 'to'
    data: any
    validate: any
  }>({
    show: false,
    name: 'to',
    data: {},
    validate: {},
  })

  const {stringYup, numberYup, infoYup, orderYup, regexYup} = UseYupValidate()

  const [scrollToErrors, setScrollToErrors] = useState<boolean>(false)
  const [currentDimensionUnit, setCurrentDimensionUnit] = useState<string>(DIMENSION_UNIT.IN)
  const [currentWeightUnit, setCurrentWeightUnit] = useState<string>(WEIGHT_UNIT.OZ)
  const {statesGlobal} = useStatesGlobal()

  const carriers = statesGlobal.settingsConfig.carriers

  const validateAddress = useCallback(
    (name) => {
      return {
        [`${name}_company`]: Yup.string().when([`${name}_first_name`], {
          is: (first_name) => !first_name || first_name.trim() === '',
          then: orderYup.companyFirstName('COMPANY'),
          otherwise: stringYup(255, 'COMPANY', false),
        }),
        [`${name}_first_name`]: Yup.string().when(`${name}_company`, {
          is: (company) => !company || company.trim() === '',
          then: orderYup.companyFirstName('FIRST_NAME'),
          otherwise: stringYup(255, 'FIRST_NAME', false),
        }),
        [`${name}_street1`]: stringYup(255, 'ADDRESS'),
        [`${name}_city`]: stringYup(255, 'CITY'),
        [`${name}_state`]: Yup.string().when(`${name}_country`, {
          is: (val) => val === 'US',
          then: regexYup.state(),
          otherwise: stringYup(255, 'STATE'),
        }),
        [`${name}_country`]: regexYup.country(),
        [`${name}_zip`]: Yup.string().when(`${name}_country`, {
          is: (val) => val === 'US',
          then: regexYup.zipCode(),
          otherwise: stringYup(255, 'POSTAL_CODE'),
        }),
        [`${name}_last_name`]: stringYup(255, 'LAST_NAME', false),
        [`${name}_street2`]: stringYup(255, 'ADDRESS_2', false),
        [`${name}_phone`]: infoYup.phone('PHONE', false),
        [`${name}_email`]: infoYup.email('EMAIL', false),
      }
    },
    [orderYup, regexYup, stringYup, infoYup]
  )

  const orderItemsValidation = useMemo(() => {
    return Yup.object().shape({
      name: stringYup(255, 'NAME'),
      sku: stringYup(70, 'SKU', false),
      qty: numberYup.quantity('QUANTITY', false),
      unit_price: numberYup.unitPrice('PRICE', false),
      content: orderYup.orderA1.content,
      description: orderYup.orderA1.description,
      hs_tariff_number: orderYup.orderA1.htsCode(),
      country: orderYup.orderA1.country,
      weight: numberYup.parcel('weight'),
      quantity: orderYup.orderA1.quantity,
      value: orderYup.orderA1.value,
      weight_unit: stringYup('no_limit', 'UNIT'),
    })
  }, [orderYup.orderA1, stringYup, numberYup])

  const parcelItemsValidation = useMemo(() => {
    return Yup.object().shape(
      OrdersConfig.PARCEL.reduce((initParcel, currentName) => {
        switch (currentName) {
          case 'length':
          case 'width':
          case 'height':
          case 'weight':
          case 'package_count':
            initParcel[currentName] = numberYup.parcel(currentName)
            break
          default:
            initParcel[currentName] = stringYup(50, 'UNIT')
            break
        }
        return initParcel
      }, {})
    )
  }, [numberYup, stringYup])

  const initialValues = useMemo(() => {
    return {
      to: {
        to_company: data?.order_ext?.to_company || null,
        to_first_name: data?.order_ext?.to_first_name || null,
        to_last_name: data?.order_ext?.to_last_name || null,
        to_street1: data?.order_ext?.to_street1 || null,
        to_street2: data?.order_ext?.to_street2 || null,
        to_city: data?.order_ext?.to_city || null,
        to_state: data?.order_ext?.to_state || null,
        to_zip: data?.order_ext?.to_zip || null,
        to_country: data?.order_ext?.to_country || null,
        to_phone: data?.order_ext?.to_phone || null,
        to_email: data?.order_ext?.to_email || null,
        to_is_residential: !!data?.order_ext?.to_is_residential || false,
      },
      service: data?.carrier || data?.service ? `${data?.carrier}_${data?.service}` : null,
      parcels: {},
      check_weight: false,
      container_id: data?.container_id || null,
      reference1: data?.reference_1 || null,
      reference2: data?.reference_2 || null,
      order_items: {},
      check_package_count: false,
    }
  }, [data])

  const initValidateSchema = useMemo(
    () => ({
      to: Yup.object().shape(validateAddress('to'), [['to_first_name', 'to_company']]),
      service: stringYup(255, 'SERVICE'),
      parcels: Yup.object(),
      check_weight: orderYup.checkIntlWeight,
      container_id: orderYup.orderA1.containerId(false),
      reference1: orderYup.reference('REF_1'),
      reference2: orderYup.reference('REF_2'),
      order_items: Yup.object(),
      check_package_count: orderYup.checkPackageCount,
    }),
    [orderYup, stringYup, validateAddress]
  )
  const [validateSchema, setValidateSchema] = useState<any>(initValidateSchema)

  const formik = useFormik({
    initialValues,
    enableReinitialize: false,
    validationSchema: Yup.object().shape(validateSchema),
    onSubmit: () => {},
  })
  const handleCloseFromToModal = () => {
    setFormToModal({
      show: false,
      name: 'to',
      data: {},
      validate: {},
    })
  }

  const handleSaveModal = (name, values) => {
    formik.setFieldValue(name, values)
    handleCloseFromToModal()
  }

  const initOrderItems = async () => {
    if (isEmpty(data?.order_items)) return
    const orderItems = {}
    const orderItemsSchema = {}

    data?.order_items.forEach((item) => {
      row.current.order_items++
      orderItems[row.current.order_items] = {
        id: item?.id || null,
        name: item?.name || null,
        sku: item?.sku || null,
        unit_price: item?.unit_price || null,
        qty: item?.quantity || null,
        content: item?.a1_content || null,
        description: item?.a1_description || null,
        hs_tariff_number: item?.a1_hs_tariff_number || null,
        country: item?.a1_country || null,
        weight: Number(item?.a1_weight) || null,
        quantity: item?.a1_quantity || null,
        value: Number(item?.a1_value) || null,
        weight_unit: item?.a1_weight_unit || null,
      }
      orderItemsSchema[row.current.order_items] = orderItemsValidation
    })

    await formik.setFieldValue('order_items', {
      ...formik.values.order_items,
      ...orderItems,
    })

    await setValidateSchema((prev) => ({
      ...prev,
      order_items: Yup.object().shape({
        ...prev?.order_items?.fields,
        ...orderItemsSchema,
      }),
    }))
  }

  const initParcelItems = async () => {
    if (isEmpty(data?.order_parcels)) return
    const items = {}
    const schema = {}

    data?.order_parcels.forEach((item) => {
      row.current.parcels++
      items[row.current.parcels] = {
        id: item?.id || null,
        package_type: item?.package_type || '',
        package_id: item?.package_id || null,
        weight: Number(item?.weight) || null,
        weight_unit: item?.weight_unit || WEIGHT_UNIT.OZ,
        length: Number(item?.length) || null,
        width: Number(item?.width) || null,
        height: Number(item?.height) || null,
        dimension_unit: item?.dimension_unit || DIMENSION_UNIT.IN,
        package_count: item?.package_count || 1,
      }
      schema[row.current.parcels] = parcelItemsValidation
    })

    await formik.setFieldValue('parcels', {
      ...formik.values.parcels,
      ...items,
    })

    await setValidateSchema((prev) => ({
      ...prev,
      parcels: Yup.object().shape({
        ...prev?.parcels?.fields,
        ...schema,
      }),
    }))
  }

  useEffect(() => {
    initOrderItems()
    initParcelItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.initialValues])

  useEffect(() => {
    const errorPaths = getErrorPaths(formik.errors)

    if (errorPaths.length > 0 && scrollToErrors) {
      const firstErrorPath = errorPaths.sort((a, b) => getKeyOrder(a) - getKeyOrder(b))[0]
      const input: any = document.querySelector(`[name="${firstErrorPath}"]`) as HTMLElement

      if (input) {
        const elementToScroll = input.type === 'hidden' ? input.closest('div') : input

        if (elementToScroll) {
          setTimeout(() => {
            elementToScroll.scrollIntoView({behavior: 'smooth', block: 'center'})
            if (elementToScroll.type !== 'hidden') {
              elementToScroll.focus()
            }
          }, 0)
          setScrollToErrors(false)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToErrors, formik.errors])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteOrderItem = async (key: string) => {
    await setValidateSchema((prev) => {
      const updatedFields = {...prev?.order_items?.fields}
      delete updatedFields[key]
      return {
        ...prev,
        order_items: Yup.object().shape(updatedFields),
      }
    })

    const updatedOrderItems = {...formik.values.order_items}
    delete updatedOrderItems[key]
    await formik.setFieldValue('order_items', updatedOrderItems)
  }

  const newItems = useMemo(() => {
    return {
      parcels: {
        value: {
          package_type: null,
          weight: null,
          weight_unit: currentWeightUnit,
          length: null,
          width: null,
          height: null,
          dimension_unit: currentDimensionUnit,
          package_count: 1,
        },
        schema: parcelItemsValidation,
      },
      order_items: {
        value: {
          id: null,
          content: '',
          name: '',
          sku: '',
          qty: '',
          unit_price: '',
          description: '',
          hs_tariff_number: '',
          country: '',
          weight: '',
          quantity: '',
          value: '',
          weight_unit: '',
        },
        schema: orderItemsValidation,
      },
    }
  }, [currentDimensionUnit, currentWeightUnit, orderItemsValidation, parcelItemsValidation])

  const handleAddNewItem = async (name: string) => {
    const currentIndex = row.current[name]
    const nextIndex = currentIndex + 1
    const newItem = cloneDeep(newItems[name])

    const newItemValue = {
      [nextIndex]: newItem.value,
    }

    const updatedItems = {...formik.values[name], ...newItemValue}

    // Set package_id of first parcel item to null when adding new parcel
    if (name === 'parcels') {
      const firstKey = Object.keys(updatedItems)?.[0] || '0'
      updatedItems[firstKey] = {
        ...updatedItems[firstKey],
        package_id: null,
        package_type: null,
        weight_limit: null,
      }
    }

    await formik.setFieldValue(name, updatedItems)

    await setValidateSchema((prev) => {
      return {
        ...prev,
        [name]: Yup.object().shape({
          ...prev?.[name]?.fields,
          [nextIndex]: newItem.schema,
        }),
      }
    })
    row.current[name] = nextIndex
  }

  const handleDeleteParcelItem = async (name: string, key: string) => {
    await setValidateSchema((prev) => {
      const updatedFields = {...prev?.parcels?.fields}
      delete updatedFields[key]
      return {
        ...prev,
        parcels: Yup.object().shape(updatedFields),
      }
    })

    const updatedParcels = {...formik.values.parcels}
    delete updatedParcels[key]
    await formik.setFieldValue('parcels', updatedParcels)
  }

  // BEGIN: Ship To
  const shipToShow = useMemo(() => {
    const {to_street1, to_street2, to_city, to_state, to_zip} = formik.values.to
    const addressTo = compact([
      to_street1,
      to_street2,
      to_city,
      to_state && to_zip ? `${to_state} ${to_zip}` : to_state || to_zip,
    ]).join(', ')
    return addressTo
  }, [formik.values.to])

  const checkValidateFailed = useMemo(() => {
    if (!formik.values.to.to_company && !formik.values.to.to_first_name) {
      return true
    }
    const validate = ['to_city', 'to_state', 'to_country', 'to_zip', 'to_street1']
    return validate.some((item) => !formik.values.to[item])
  }, [formik.values])
  // END: Ship To

  // BEGIN: Total Weight
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const totalWeightPackage = useMemo(
    () =>
      Object.values(formik.values.parcels).reduce(
        (sum: number, parcel: any) => sum + (parseFloat(parcel?.weight) || 0),
        0
      ),
    [formik.values.parcels]
  )
  // END: Total Weight

  const totalOrderItemsWeight = useMemo(() => {
    const parcelUnit = get(Object.values(formik.values.parcels), '[0].weight_unit', WEIGHT_UNIT.OZ)

    return Object.values(formik.values.order_items).reduce((sum: number, item: any) => {
      const itemTotalWeight = parseFloat(item?.weight || 0) * item.quantity
      const converted = convertUnit(
        {unit: item.weight_unit, value: itemTotalWeight},
        {unit: parcelUnit}
      )

      return sum + converted
    }, 0)
  }, [formik.values.order_items, formik.values.parcels])

  const totalParcelsWeight = useMemo(() => {
    const parcels = Object.values(formik.values.parcels)
    const parcelUnit = get(parcels, '[0].weight_unit', WEIGHT_UNIT.OZ)

    return Object.values(parcels).reduce((sum: number, item: any) => {
      const itemTotalWeight = parseFloat(item?.weight || 0)
      const converted = convertUnit(
        {unit: item.weight_unit, value: itemTotalWeight},
        {unit: parcelUnit}
      )
      return sum + converted
    }, 0)
  }, [formik.values.parcels])

  const optionsService = useMemo(() => {
    if (isEmpty(carriers)) return
    const domesticCarriers = Object.values(cloneDeep(carriers)).filter(
      (carrier: any) => !carrier?.international && carrier?.status
    )

    return [
      {
        label: intl.formatMessage({id: 'DOMESTIC'}),
        options: domesticCarriers.map((carrier: any) => ({
          label: carrier?.visuals?.display_name,
          value: carrier?.carrier_service,
          carrier: carrier?.carrier,
        })),
      },
    ]
  }, [carriers, intl])

  useEffect(() => {
    if (!formik.values.order_items) return

    const weightExceeded =
      Math.round(totalOrderItemsWeight * 100) / 100 > Math.round(totalParcelsWeight * 100) / 100

    formik.setFieldValue('check_weight', weightExceeded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.order_items,
    formik.values.parcels,
    convertUnit,
    totalOrderItemsWeight,
    totalParcelsWeight,
  ])

  return (
    <>
      {fromToModal.show && fromToModal.name === 'to' && (
        <FromOrToModal
          data={fromToModal}
          handleSave={handleSaveModal}
          handleClose={handleCloseFromToModal}
          disabled={true}
        />
      )}
      <Modal
        id='gori_modal_edit_order_info'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='mw-800px h-auto'
        show={show && !fromToModal.show}
        backdrop='static'
        onHide={handleClose}
      >
        <div className='modal-content'>
          <Modal.Header closeButton>
            <Modal.Title bsPrefix={'fw-bolder fs-1'}>
              {intl.formatMessage({id: 'ORDER_INFORMATION'})}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className={clsx('vh-75 scroll-y', {'cursor-no-drop': true})}>
            <div className='d-flex flex-column gap-4 px-10 px-md-4'>
              <div className='d-flex flex-column flex-md-row col-12'>
                <div className='col-12 col-md-6'>
                  <label className='form-label col-3 col-md-6'>
                    {intl.formatMessage({id: 'DATE'})}
                  </label>
                  <span className='col-8 text-dark fw-bolder'>
                    {convertUserTimeZone(data?.created_at, currentUser, FORMAT_DATE.DATE)}
                  </span>
                </div>
                <div className='col-12 col-md-6'>
                  <label className='form-label col-3'>{intl.formatMessage({id: 'BAE_ID'})}</label>
                  <span className='col-8 text-dark fw-bolder'>{data?.bae_id}</span>
                </div>
              </div>
              <div className='d-flex flex-column'>
                <div className='d-flex'>
                  <label className='form-label col-form-label required col-3'>
                    {intl.formatMessage({id: 'SHIP_TO'})}
                  </label>
                  <div className='input-custom col-6'>
                    <input
                      value={shipToShow}
                      className={clsx('form-control input-custom__input cursor-pointer', {
                        'is-invalid': getIn(formik.touched, 'to') && checkValidateFailed,
                        'is-valid': getIn(formik.touched, 'to') && !checkValidateFailed,
                      })}
                      onClick={() => {
                        setFormToModal({
                          show: true,
                          name: 'to',
                          data: formik.getFieldProps('to').value,
                          validate: {},
                        })
                      }}
                    />
                  </div>
                </div>
                <div className='d-flex mt-1'>
                  <div className='col-3' />
                  {checkValidateFailed && getIn(formik.touched, 'to') && (
                    <span className='text-danger fs-7'>{intl.formatMessage({id: 'REQUIRED'})}</span>
                  )}
                </div>
              </div>
              <div className={clsx('d-flex flex-column gap-4', {'pe-none': true})}>
                <div className='d-flex col-12'>
                  <SelectFormik
                    className='col-6'
                    labelClassName='col-3 col-form-label'
                    label={intl.formatMessage({id: 'SERVICE'})}
                    options={optionsService || [{label: '', options: []}]}
                    formik={formik}
                    name='service'
                    required
                    emptyDefault={false}
                  />
                </div>

                <div className='d-flex col-12'>
                  <PackageInfo
                    formik={formik}
                    // disabled={isDisabled}
                    handleAddNewItem={() => handleAddNewItem('parcels')}
                    handleDeleteItem={handleDeleteParcelItem}
                    currentDimensionUnit={currentDimensionUnit}
                    setCurrentDimensionUnit={setCurrentDimensionUnit}
                    currentWeightUnit={currentWeightUnit}
                    setCurrentWeightUnit={setCurrentWeightUnit}
                  />
                </div>
                <div className='d-flex col-12 col-md-6'>
                  <InputTextFormik
                    className='col-6'
                    labelClassName='col-6 col-form-label'
                    label={intl.formatMessage({id: 'CONTAINER_ID'})}
                    formik={formik}
                    name={'container_id'}
                  />
                </div>
                <div className='d-flex flex-column flex-md-row col-12 gap-4'>
                  <div className='d-flex col-12 col-md-6'>
                    <InputTextFormik
                      labelClassName='col-6 col-form-label'
                      label={intl.formatMessage({id: 'REF_1'})}
                      formik={formik}
                      name={'reference1'}
                    />
                  </div>
                  <div className='d-flex col-12 col-md-6'>
                    <InputTextFormik
                      labelClassName='col-6 col-form-label text-start text-md-center'
                      label={intl.formatMessage({id: 'REF_2'})}
                      formik={formik}
                      name={'reference2'}
                    />
                  </div>
                </div>
                <hr className='text-gray-700' />
                <div>
                  {!isEmpty(formik?.values?.order_items) &&
                    Object.entries(formik?.values?.order_items).map(([key, value], index) => (
                      <div
                        className='bg-light rounded-2 p-5 mb-6 d-flex flex-column gap-4'
                        key={index}
                      >
                        <div className='d-flex justify-content-between align-items-center fs-5 fw-bolder'>
                          {intl.formatMessage({id: 'ITEM'})} {index + 1}
                        </div>
                        <span className='text-gray-700 fw-bold'>
                          {intl.formatMessage({id: 'ITEM_DETAIL'})}
                        </span>
                        <div className='d-flex'>
                          <InputTextFormik
                            className='col-6'
                            labelClassName='col-6 col-md-3 col-form-label'
                            label={intl.formatMessage({id: 'NAME'})}
                            formik={formik}
                            required
                            name={`order_items.${key}.name`}
                          />
                        </div>
                        <div className='d-flex'>
                          <InputTextFormik
                            className='col-6'
                            labelClassName='col-6 col-md-3 col-form-label'
                            label={intl.formatMessage({id: 'SKU'})}
                            formik={formik}
                            name={`order_items.${key}.sku`}
                          />
                        </div>
                        <div className='d-flex flex-column flex-md-row gap-4 gap-md-0'>
                          <div className='d-flex col-12 col-md-6'>
                            <InputTextFormik
                              className='col-6'
                              labelClassName='col-6 col-form-label'
                              label={intl.formatMessage({id: 'QUANTITY'})}
                              formik={formik}
                              type='number'
                              isInteger
                              name={`order_items.${key}.qty`}
                            />
                          </div>
                          <div className='d-flex col-12 col-md-6'>
                            <InputTextFormik
                              className='col-6'
                              labelClassName='col-6 col-form-label text-start text-md-center'
                              label={`${intl.formatMessage({id: 'PRICE'})} ($)`}
                              formik={formik}
                              type='number'
                              name={`order_items.${key}.unit_price`}
                            />
                          </div>
                        </div>
                        <hr className='text-gray-700' />
                        <span className='text-gray-700 fw-bold'>
                          {intl.formatMessage({id: 'CUSTOMS_DECLARATION_INFORMATION'})}
                        </span>
                        <div className='d-flex'>
                          <SelectFormik
                            className='col-6'
                            labelClassName='col-6 col-md-3 col-form-label'
                            label={intl.formatMessage({id: 'CONTENT'})}
                            formik={formik}
                            options={A1_CONSTANTS.OPTIONS_CONTENT_TYPE}
                            required
                            name={`order_items.${key}.content`}
                            hasUseIntl={true}
                            emptyDefault={false}
                          />
                        </div>
                        <div className='d-flex'>
                          <InputTextFormik
                            className='col-6'
                            labelClassName='col-6 col-md-3 col-form-label'
                            label={intl.formatMessage({id: 'DESCRIPTION'})}
                            formik={formik}
                            required
                            name={`order_items.${key}.description`}
                          />
                        </div>
                        <div className='d-flex'>
                          <InputTextFormik
                            className='col-6'
                            labelClassName='col-6 col-md-3 col-form-label'
                            label={intl.formatMessage({id: 'HTS_CODE'})}
                            formik={formik}
                            required
                            name={`order_items.${key}.hs_tariff_number`}
                          />
                        </div>
                        <div className='d-flex'>
                          <SelectFormik
                            className='col-6'
                            labelClassName='col-6 col-md-3 col-form-label'
                            label={intl.formatMessage({id: 'COUNTRY_OF_ORIGIN'})}
                            formik={formik}
                            options={OPTION_COUNTRIES}
                            required
                            name={`order_items.${key}.country`}
                            emptyDefault={false}
                          />
                        </div>
                        <div className='d-flex flex-column flex-md-row gap-4 gap-md-0'>
                          <div className='d-flex col-12 col-md-6'>
                            <InputTextFormik
                              className='col-6'
                              labelClassName='col-6 col-form-label'
                              label={intl.formatMessage({id: 'QUANTITY'})}
                              formik={formik}
                              required
                              name={`order_items.${key}.quantity`}
                            />
                          </div>
                          <div className='d-flex col-12 col-md-6'>
                            <InputTextFormik
                              className='col-6'
                              labelClassName='col-6 col-form-label text-start text-md-center'
                              label={intl.formatMessage({id: 'VALUE_$'})}
                              formik={formik}
                              required
                              name={`order_items.${key}.value`}
                            />
                          </div>
                        </div>

                        <div className='d-flex flex-column flex-md-row gap-4 gap-md-0'>
                          <div className='d-flex col-12 col-md-6'>
                            <InputTextFormik
                              className='col-6 col-md-3'
                              labelClassName='col-6 col-form-label'
                              label={intl.formatMessage({id: 'WEIGHT'})}
                              formik={formik}
                              required
                              name={`order_items.${key}.weight`}
                            />
                          </div>
                          <div className='d-flex col-12 col-md-6'>
                            <SelectFormik
                              className='col-6'
                              label={intl.formatMessage({id: 'UNIT'})}
                              placeholder=''
                              labelClassName='col-6 col-form-label text-start text-md-center'
                              options={OPTION_WEIGHT_UNIT}
                              name={`order_items.${key}.weight_unit`}
                              formik={formik}
                              required
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className='d-flex justify-content-end'>
              <Button
                className='btn btn-secondary me-2'
                label={intl.formatMessage({id: 'CANCEL'})}
                loadingText={intl.formatMessage({id: 'CANCEL'})}
                event={handleClose}
              />
            </div>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  )
}

export {EditOrderInfo}
